import { cn } from "@/lib/utils";
import { Check, ChevronRight } from "lucide-react";
import Link from "next/link";
import { BsFillTrophyFill, BsLightningChargeFill } from "react-icons/bs";
import { FaCrown } from "react-icons/fa6";

export const PURCHASE_PLANS = [
  {
    name: "Hobby",
    monthly: { price: 19 },
    yearly: { price: 160 },
    values: {
      videos: 50,
      exports: 40 * 60,
      voiceovers: 30 * 60,
      images: 100,
    },
    icon: BsLightningChargeFill,
    features: ["40 minutes of export", "30 voiceover minutes", "100 AI images"],
  },
  {
    name: "Clipper",
    monthly: { price: 39 },
    yearly: { price: 327 },
    values: {
      videos: 150,
      exports: 120 * 60,
      voiceovers: 120 * 60,
      images: 300,
    },
    icon: FaCrown,
    features: ["2 hours of export", "120 voiceover minutes", "300 AI images"],
  },
  {
    name: "Pro",
    monthly: { price: 79 },
    yearly: { price: 664 },
    values: {
      videos: 250,
      exports: 240 * 60,
      voiceovers: 180 * 60,
      images: 500,
    },
    icon: BsFillTrophyFill,
    features: ["3 hours of export", "180 voiceover minutes", "500 AI images"],
  },
];

type PlanCardProps = {
  term: "monthly" | "yearly";
  plan: (typeof PURCHASE_PLANS)[number];
};

export const PlanCard = ({ term, plan }: PlanCardProps) => (
  <div className="relative flex w-full justify-center">
    {plan.name === "Clipper" && (
      <div className="absolute -top-1 h-[50px] w-full rounded-t-2xl bg-[#335CFF] py-2.5 @lg:-top-9">
        <p className="text-center text-xs tracking-[0.2em] text-white @xl:tracking-[0.3em] @2xl:text-sm">
          MOST POPULAR!
        </p>
      </div>
    )}
    <div
      className={cn(
        "relative flex w-full flex-col items-center justify-center space-y-8 rounded-xl bg-white shadow-lg",
        "max-w-xs @lg:max-w-sm @2xl:max-w-md",
        "px-7 pb-7 pt-8 @lg:px-3.5 @lg:py-3.5 @2xl:px-5 @2xl:pb-7 @2xl:pt-8",
        "border border-gray-100",
        plan.name === "Clipper"
          ? "mt-8 border-[#335CFF] shadow-[#DFE7FF] @lg:mt-0"
          : "border-gray-100",
      )}
    >
      <div className="flex w-full flex-col items-start space-y-3 overflow-visible">
        <div>
          <div className="w-fit rounded-full border border-[#E5E7ED] bg-white p-3">
            <plan.icon className="size-5 @lg:size-4" style={{ fill: "url(#gradient)" }} />
            <svg width="0" height="0">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="40%" style={{ stopColor: "#82B6FF", stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: "#000D49", stopOpacity: 1 }} />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <p className="text-[20px]/10 font-semibold text-[#101828]">{plan.name}</p>
        </div>
        <div>
          <p
            className={cn(
              " font-bold text-[#101828]",
              "text-5xl @lg:text-3xl @xl:text-4xl @2xl:text-5xl",
            )}
          >{`$${term === "monthly" ? plan[term].price : (plan[term].price / 12).toFixed(0)}/mo`}</p>

          <p className={cn("text-sm/9 text-[#475467]/90", "hidden @lg:inline @2xl:hidden")}>
            {plan.values.videos} AI videos/mo
          </p>
          <p className="text-base/9 text-[#475467]/90 @lg:hidden @2xl:inline @2xl:text-sm/9 @3xl:text-base/9">
            {plan.values.videos} AI videos per month
          </p>
        </div>
        {term === "yearly" && (
          <p className="mt-2 text-sm text-[#475467]">{`Billed yearly at $${plan[term].price}`}</p>
        )}

        <div className="flex w-full flex-col space-y-3 pt-3">
          <div className="flex flex-col space-y-3">
            {plan.features.map((feature, i) => {
              return (
                <div key={i} className="flex flex-row items-center justify-start space-x-2">
                  <div className="flex items-center justify-center rounded-full bg-[#D1FADF] p-1">
                    <Check className="size-3.5 text-[#12B76A] @lg:size-3" strokeWidth={3} />
                  </div>
                  <p
                    className={cn(
                      "text-base text-[#475467] @lg:text-sm @4xl:text-base",
                      "@lg:hidden @3xl:inline",
                    )}
                  >
                    {feature}
                  </p>
                  <p
                    className={cn(
                      "text-xs text-[#475467] @2xl:text-sm",
                      "hidden @lg:inline @3xl:hidden",
                    )}
                  >
                    {feature.replace("minutes", "min").replace("hours", "hrs")}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Link href="https://crayo.ai/dashboard" className="w-full">
        <button
          className={cn(
            "flex w-full cursor-pointer items-center justify-center rounded-full px-3 py-2.5 transition-colors duration-200 ease-in-out",
            "border border-gray-200 text-[#1A191A]/70 hover:bg-gray-50",
            plan.name === "Clipper" && "bg-[#4D71FF] text-white hover:bg-[#4D71FF]/90",
          )}
        >
          <p
            className={cn(
              "flex items-center justify-center gap-1 text-center text-base font-medium",
              plan.name === "Clipper" ? "text-white" : "text-[#1A191A]/70",
            )}
          >
            Choose
            <span className="@lg:hidden @2xl:inline">{plan.name}</span>
            <ChevronRight className="ml-1 h-4 w-4" strokeWidth={3} />
          </p>
        </button>
      </Link>
    </div>
  </div>
);
